import {WidgetFullEditorHostContext} from "../common/WidgetFullEditorPortal"
import {cancelEditWidget, ShapeWidgetData, useAppDispatch} from '@dv5/domain'
import React, {FC, useContext, useEffect, useState} from 'react'
import {isModPressed} from '@dv5/components'
import {useShapeWidgetUpdate} from "./useShapeWidgetUpdate"
import {WidgetPlainText} from "../common/WidgetPlainText"

interface Props {
    canvasId: string
    widgetId: string
    data: ShapeWidgetData
    text: string
    containerRef: React.RefObject<HTMLDivElement | null>
}

export const ShapeWidgetInlineEditor: FC<Props> = ({
    canvasId,
    widgetId,
    data,
    text,
    containerRef,
}) => {
    const [editingText, setEditingText] = useState(text)
    const fullEditorHost = useContext(WidgetFullEditorHostContext)
    const dispatch = useAppDispatch()
    const { updateData } = useShapeWidgetUpdate(canvasId, widgetId, data)

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node
            if (containerRef.current && !containerRef.current.contains(target) && !fullEditorHost?.contains(target)) {
                if (editingText !== text) {
                    saveText(editingText)
                } else {
                    onCancelEdit()
                }
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [text, editingText, fullEditorHost])

    const onCancelEdit = () => {
        dispatch(cancelEditWidget({
            canvasId: canvasId,
            changes: widgetId,
        }))
    }

    const saveText = (newText: string) => {
        if (newText !== text) {
            updateData(['text'], {text: newText})
        }
    }

    useEffect(() => {
        setEditingText(text)
    }, [text])

    const changeText = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setEditingText(e.target.value)
    }

    const keyHandler = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && isModPressed(event)) {
            event.preventDefault()
            event.stopPropagation()
            saveText(editingText)
        } else if (event.key === 'Escape') {
            event.preventDefault()
            event.stopPropagation()
            onCancelEdit()
        }
    }

    return (
        <WidgetPlainText
            text={editingText}
            editorProps={{
                onChange: changeText,
                onKeyDown: keyHandler,
                autoFocus: true,
                'aria-label': 'Shape text',
            }}
        />
    )
}
